import { AuxSecondaryNavigationOption, AuxButton, AuxProgressIndicator } from "@blk/aladdin-react-components-es";
import { AuxButtonTypeEnum, AuxButtonSizeEnum, AuxProgressIndicatorSizeEnum, AuxProgressIndicatorTypeEnum } from "@blk/aladdin-web-components";
import { replace } from "connected-react-router";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPanels, useHandleOnAddAnalyses } from "./helper/analysisHelpers";
import { filterAvailableAnalysesByAssetClass, makeFilterAnalysesByAvailableModulesSelector } from "./hooks/handleCurrentDashboard";
import { useGetCurrentWorkspace } from "./hooks/handleWorkspacePreferences";
import { useGetEntityInfo } from "./hooks/use-get-dashboards-by-type";
import { InsWorkspaceAnalysisEditMenu } from "./insWorkspaceAnalysisEditMenu";
import { globalWorkspaceSelector } from "./insWorkspaceLeftPane";
import { useCreateAnalysisMutation, useListAnalysesQuery } from "./store/api";
import { DashboardTypes } from "./hooks/types";
import { PageType, workspaceEntityTypeDataToEntityTypeMap } from "./store/types";
import { printBadgeProps } from "components/print-preview/helper/page-constants";
import { InsAccordion } from "components/ux";
import { useCurrentDashboardType, useDashboardFullPathInformations } from "components/workspaces/hooks/use-current-dashboard";
import { usePermission, Permissions } from "feature-flag";
import { appInsightEvents, language } from "language/default";
import "../workspaces/left-pane/insDesignerLeftPane.scss";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { getCurrentLocalTime } from "common/date-utils";
import { useWorkspaceNavigation } from "./hooks/useWorkspaceNavigation";
import { useAllWorkspacesSelector } from "./hooks";
// Module-scoped helpers to reduce statements inside the component
const spacerStyle = { height: "3.5rem" };
const getOptionKey = (section) => `/${section}`;
const checkIfSectionIsPrintMode = (sections, sectionId) => {
    const currentSection = sections[sectionId];
    if (currentSection && currentSection.type === PageType.PRINT) {
        return printBadgeProps;
    }
    return undefined;
};
export const renderBadgeComponents = (index, relevantAnalysis, selectedType) => (selectedType === DashboardTypes.FundSeries
    ? React.createElement(React.Fragment, null)
    : React.createElement(InsWorkspaceAnalysisEditMenu, { panelIndex: index, analysisToEdit: relevantAnalysis?.[index], analysesList: relevantAnalysis }));
export const changeUrl = (sourceItem, pathInformations, workspace, currentDashboardType, dispatch) => {
    const action = replace(`${pathInformations.slice(0, 3).join("/")}/${workspace?.id}/${sourceItem.detail?.element?.value?.newSection}`);
    if (action) {
        dispatch(action);
    }
    appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.OpenSection, properties: { LocalTimestamp: getCurrentLocalTime() } }, { entity: currentDashboardType, sectionName: sourceItem.detail?.displayValue, workspaceName: workspace?.name || "" });
};
export const InsWorkspaceAnalysisPane = () => {
    const { analyses: analysesList, currentWorkspace, workspacesAndPreferences } = useSelector((state) => globalWorkspaceSelector(state)), { pathInformations, selectedSection, selectedType } = useDashboardFullPathInformations(), { selectedWorkspace } = useDashboardFullPathInformations(), accessSharedFolderPermission = usePermission([Permissions.ACCESS_SHARED_FOLDER]), currentDashboardType = useCurrentDashboardType(), dispatch = useDispatch(), dynamicLoadingText = useAllWorkspacesSelector((o) => o.dynamicLoadingText), entityInfo = useGetEntityInfo(), isRegularLeftPaneOpen = useSelector((state) => state.leftPane[selectedType]?.isOpen) ?? true, regularButtonClassName = isRegularLeftPaneOpen ? "sideMenuButtonOpen" : "sideMenuButtonClosed", { assetClasses } = entityInfo;
    const entityType = workspaceEntityTypeDataToEntityTypeMap(entityInfo.type, entityInfo.subtype);
    const { isFetching } = useListAnalysesQuery({
        entityType,
        id: selectedWorkspace ?? currentWorkspace?.id
    }, {
        refetchOnMountOrArgChange: true,
        selectFromResult: (result) => ({
            isFetching: result.isFetching
        }),
        skip: !selectedWorkspace && !currentWorkspace?.id
    });
    const filteredSelector = useMemo(() => makeFilterAnalysesByAvailableModulesSelector(analysesList), [analysesList]);
    const filteredAnalysesList = useSelector((state) => filteredSelector(state));
    const workspace = useGetCurrentWorkspace(currentWorkspace, workspacesAndPreferences);
    const relevantAnalysis = useMemo(() => {
        const arr = filterAvailableAnalysesByAssetClass(filteredAnalysesList, assetClasses);
        if (!arr) {
            return arr;
        }
        // Sort once per dependency change
        return [...arr].sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
    }, [filteredAnalysesList, assetClasses]);
    const panels = useMemo(() => getPanels(relevantAnalysis), [relevantAnalysis]);
    const { handleChangePage } = useWorkspaceNavigation({
        changeUrl,
        currentDashboardType,
        dispatch,
        pathInformations,
        selectedSection,
        selectedType,
        workspace
    });
    const sections = useSelector((state) => state.entities.Section);
    const [createAnalysis] = useCreateAnalysisMutation();
    const { handleOnAddAnalyses } = useHandleOnAddAnalyses(currentWorkspace, entityInfo, createAnalysis, accessSharedFolderPermission);
    const createAuxSecondaryNavigationOption = useCallback((displayValue, panelId, section) => React.createElement(AuxSecondaryNavigationOption, { badgeProps: checkIfSectionIsPrintMode(sections, section), key: getOptionKey(section), displayValue: displayValue, isSelected: section === selectedSection, onOptionSelected: handleChangePage, value: { newSection: section } }), [sections, handleChangePage, selectedSection]);
    const badge = useCallback((index) => renderBadgeComponents(index, relevantAnalysis || [], selectedType), [relevantAnalysis, selectedType]);
    return React.createElement(React.Fragment, null,
        React.createElement(InsAccordion, { onCreateNavigationOption: createAuxSecondaryNavigationOption, paneId: selectedType, panels: panels, badge: badge }),
        React.createElement("div", { style: spacerStyle }),
        React.createElement("div", { className: regularButtonClassName }, isRegularLeftPaneOpen
            ? React.createElement(AuxButton, { isDisabled: !currentWorkspace?.isEditable || (!currentWorkspace.isPrivate && !accessSharedFolderPermission), onClick: handleOnAddAnalyses, icon: "add", label: language.en.dashboard.addAnalysis, type: AuxButtonTypeEnum.SECONDARY, size: AuxButtonSizeEnum.SMALL })
            : React.createElement(React.Fragment, null)),
        isFetching && React.createElement(AuxProgressIndicator, { isSpinner: true, isOverlay: true, hasLabel: true, loadingLabel: dynamicLoadingText, hasCounter: false, size: AuxProgressIndicatorSizeEnum.LARGE, type: AuxProgressIndicatorTypeEnum.LOADING }));
};
